<template>
    <main class="evv-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('events.page.title') }}</div>
        </portal>

        <div v-if="loading" class="evv-loader">
            <BmvLoader />
        </div>

        <template v-else-if="event">
            <div v-if="event.cover_media" class="evv-cover">
                <img
                    :src="event.cover_media.url || event.cover_media.path"
                    :alt="event.title"
                    class="evv-cover-img"
                />
            </div>

            <header class="evv-hero">
                <div class="evv-hero-left">
                    <div class="evv-badges">
                        <span class="ev-badge" :class="`ev-badge--${event.type}`">
                            <i :class="event.type === 'event' ? 'fal fa-calendar-alt' : 'fal fa-project-diagram'"></i>
                            {{ $t(`events.type.${event.type}`) }}
                        </span>
                        <span class="ev-badge" :class="`ev-badge--${event.status}`">
                            <i :class="statusIcon(event.status)"></i>
                            {{ $t(`events.status.${event.status}`) }}
                        </span>
                        <span v-if="event.is_featured" class="ev-badge ev-badge--featured">
                            <i class="fal fa-star"></i>
                            {{ $t('events.form.is_featured') }}
                        </span>
                    </div>
                    <h1 class="evv-title">{{ event.title }}</h1>
                    <div class="evv-meta">
                        <span v-if="event.start_date">
                            <i class="fal fa-calendar"></i>
                            {{ formatDate(event.start_date) }}
                            <template v-if="event.end_date"> → {{ formatDate(event.end_date) }}</template>
                        </span>
                        <span v-if="event.location && event.location.address">
                            <i class="fal fa-map-marker-alt"></i>
                            {{ event.location.address }}
                        </span>
                        <span v-if="event.author">
                            <i class="fal fa-user"></i>
                            {{ event.author.name }}
                        </span>
                    </div>
                </div>

                <div class="evv-hero-actions">
                    <BmvButton variant="secondary" icon="fal fa-arrow-left" @click="$router.push({ name: 'EventsList' })">
                        {{ $t('common.back') }}
                    </BmvButton>
                    <BmvButton v-if="canUpdate" variant="primary" icon="fal fa-pen" @click="$router.push({ name: 'EventEdit', params: { id: event.id } })">
                        {{ $t('common.edit') }}
                    </BmvButton>
                    <BmvButton
                        v-if="canUpdate && event.status === 'draft'"
                        variant="success"
                        icon="fal fa-paper-plane"
                        :loading="busy"
                        @click="doPublish"
                    >
                        {{ $t('events.actions.publish') }}
                    </BmvButton>
                    <BmvButton
                        v-else-if="canUpdate && event.status === 'published'"
                        variant="warning"
                        icon="fal fa-ban"
                        :loading="busy"
                        @click="doUnpublish"
                    >
                        {{ $t('events.actions.unpublish') }}
                    </BmvButton>
                    <BmvButton
                        v-if="canUpdate && event.status !== 'cancelled'"
                        variant="danger"
                        icon="fal fa-times-circle"
                        :loading="busy"
                        @click="doCancel"
                    >
                        {{ $t('events.actions.cancel') }}
                    </BmvButton>
                </div>
            </header>

            <!-- Reactions -->
            <div class="evv-card evv-card--reactions">
                <h2 class="evv-section-title">{{ $t('events.reactions.section') }}</h2>
                <EventReactionsBar
                    :initial-summary="event.reactions_summary || {}"
                    :initial-total="event.reactions_total || 0"
                />
            </div>

            <div v-if="event.description" class="evv-card">
                <h2 class="evv-section-title">{{ $t('events.view.description') }}</h2>
                <div class="evv-rich-text" v-html="event.description"></div>
            </div>

            <div v-if="event.location && event.location.lat && event.location.lng" class="evv-card">
                <h2 class="evv-section-title">{{ $t('events.builder.section_location') }}</h2>
                <EventsMap :events="[event]" />
            </div>

            <div v-if="event.comments && event.comments.length" class="evv-card">
                <h2 class="evv-section-title">
                    {{ $t('events.comments.section') }}
                    <span class="evv-section-count">{{ event.comments.length }}</span>
                </h2>
                <ul class="evv-comments">
                    <li v-for="comment in event.comments" :key="comment.id" class="evv-comment">
                        <div class="evv-comment-avatar">
                            {{ initials(comment.citizen) }}
                        </div>
                        <div class="evv-comment-body">
                            <div class="evv-comment-header">
                                <span class="evv-comment-name">{{ comment.citizen ? comment.citizen.name : '—' }}</span>
                                <span class="evv-comment-date">{{ formatDate(comment.created_at) }}</span>
                            </div>
                            <p class="evv-comment-text">{{ comment.content }}</p>
                        </div>
                    </li>
                </ul>
            </div>
            <div v-else-if="event.comments && !event.comments.length" class="evv-card evv-card--empty">
                <i class="fal fa-comment-slash evv-empty-icon"></i>
                <p>{{ $t('events.comments.empty') }}</p>
            </div>
        </template>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import EventsMap from '@/components/Events/EventsMap.vue'
import EventReactionsBar from '@/components/Events/EventReactionsBar.vue'

export default {
    name: 'EventView',
    components: { BmvButton, BmvLoader, EventsMap, EventReactionsBar },
    data() {
        return { loading: false, busy: false }
    },
    computed: {
        ...mapState('events', { event: (state) => state.event }),
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canUpdate() {
            return this.permissions.includes('events.update')
        }
    },
    async created() {
        this.loading = true
        try {
            await this.getEvent(this.$route.params.id)
        } catch (err) {
            this.$store.dispatch('dispatchError', err)
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapActions('events', ['getEvent', 'publishEvent', 'unpublishEvent', 'cancelEvent']),
        statusIcon(status) {
            const icons = { draft: 'fal fa-pen', published: 'fal fa-check-circle', cancelled: 'fal fa-times-circle' }
            return icons[status] || 'fal fa-circle'
        },
        formatDate(iso) {
            if (!iso) return ''
            return this.moment(iso).format('L')
        },
        initials(citizen) {
            if (!citizen || !citizen.name) return '?'
            return citizen.name
                .split(' ')
                .map((w) => w[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()
        },
        async doPublish() {
            if (this.busy) return
            this.busy = true
            try {
                await this.publishEvent(this.event.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.published') })
                await this.getEvent(this.event.id)
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busy = false
            }
        },
        async doUnpublish() {
            if (this.busy) return
            this.busy = true
            try {
                await this.unpublishEvent(this.event.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.unpublished') })
                await this.getEvent(this.event.id)
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busy = false
            }
        },
        async doCancel() {
            if (this.busy) return
            const confirmed = this.$bmvDialog && typeof this.$bmvDialog.confirm === 'function'
                ? await this.$bmvDialog.confirm({
                    variant: 'danger',
                    title: this.$t('events.actions.cancel_confirm'),
                    confirmLabel: this.$t('events.actions.cancel'),
                    cancelLabel: this.$t('common.cancel')
                })
                : window.confirm(this.$t('events.actions.cancel_confirm'))
            if (!confirmed) return
            this.busy = true
            try {
                await this.cancelEvent(this.event.id)
                this.$store.dispatch('toast', { type: 'success', title: this.$t('events.actions.cancelled') })
                await this.getEvent(this.event.id)
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busy = false
            }
        }
    }
}
</script>

<style scoped>
.evv-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.evv-loader {
    display: flex;
    justify-content: center;
    padding: 80px 0;
}

.evv-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: var(--v2-space-6);
    flex-wrap: wrap;
}

.evv-hero-left {
    min-width: 0;
}

.evv-hero-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.evv-badges {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.evv-title {
    margin: 0 0 10px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.evv-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 0.85rem;
    color: var(--v2-text-3);
}

.evv-meta span {
    display: flex;
    align-items: center;
    gap: 6px;
}

.evv-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 24px;
    margin-bottom: var(--v2-space-4);
}

.evv-section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-text-1);
    margin: 0 0 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--v2-border);
}

.evv-rich-text {
    font-size: 0.9rem;
    line-height: 1.65;
    color: var(--v2-text-2);
}

.evv-rich-text :deep(p) { margin: 0 0 12px; }
.evv-rich-text :deep(ul), .evv-rich-text :deep(ol) { padding-left: 20px; margin-bottom: 12px; }

.evv-card--reactions {
    padding-bottom: 20px;
}

.evv-section-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-size: 0.75rem;
    font-weight: 600;
    margin-left: 8px;
    vertical-align: middle;
}

.evv-comments {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.evv-comment {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.evv-comment-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--v2-primary-soft, #e0f2fe);
    color: var(--v2-primary, #0891b2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
    letter-spacing: 0.02em;
}

.evv-comment-body {
    flex: 1;
    min-width: 0;
    background: var(--v2-surface-1);
    border-radius: var(--v2-radius-md);
    padding: 10px 14px;
}

.evv-comment-header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 4px;
    flex-wrap: wrap;
}

.evv-comment-name {
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--v2-text-1);
}

.evv-comment-date {
    font-size: 0.75rem;
    color: var(--v2-text-4);
}

.evv-comment-text {
    margin: 0;
    font-size: 0.875rem;
    color: var(--v2-text-2);
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
}

.evv-card--empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 24px;
    color: var(--v2-text-4);
    font-size: 0.875rem;
    text-align: center;
}

.evv-empty-icon {
    font-size: 2rem;
}

/* badges (shared) */
.ev-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.ev-badge--event    { background: #e0f2fe; color: #0369a1; }
.ev-badge--project  { background: #f0fdf4; color: #166534; }
.ev-badge--draft    { background: var(--v2-surface-2); color: var(--v2-text-3); }
.ev-badge--published { background: var(--v2-success-soft); color: var(--v2-success); }
.ev-badge--cancelled { background: #fef2f2; color: #b91c1c; }
.ev-badge--featured  { background: #fef9c3; color: #a16207; }

.evv-cover {
    width: 100%;
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    margin-bottom: var(--v2-space-6);
    background: var(--v2-surface-2);
    border: 1px solid var(--v2-border);
    aspect-ratio: 16 / 6;
}

.evv-cover-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

@media (max-width: 900px) {
    .evv-v2 { padding: 16px 18px 32px; }
    .evv-hero { flex-direction: column; }
    .evv-title { font-size: 1.375rem; }
    .evv-cover { aspect-ratio: 16 / 7; }
}
</style>
